import { computed, onBeforeUnmount, ref, watch } from 'vue';

/*
 * How many of *our own* modals are currently open.
 *
 * The BHW offer popup consults this before showing so it can never stack on top
 * of an existing dialog. It is a counter we maintain ourselves rather than a
 * `document.querySelector('[role="dialog"][aria-modal="true"]')` probe on
 * purpose: that selector also matches third-party markup we neither own nor
 * version. If the CookieScript banner carries those attributes the popup is
 * permanently suppressed for every visitor who ignores the consent banner --
 * i.e. exactly the first-time-visitor cohort it targets -- and if it doesn't,
 * the popup stacks on the banner. A vendor CSS release flips us between the two
 * with no deploy on our side, and with no impression metric nobody ever finds
 * out. This module is ours, so it fails loudly instead.
 */
const openCount = ref(0);

export const anyAppModalOpen = computed(() => openCount.value > 0);

/**
 * Register a modal's own open state with the registry.
 *
 * Call once from the modal's `setup`. `isOpen` may be a ref or a getter. The
 * registration removes itself on unmount, so a modal destroyed while open
 * cannot leak a permanent "a modal is open" state that would suppress the
 * popup for the rest of the session.
 */
export function useModalRegistration(isOpen) {
    // `openCount` is module state, which under SSR is shared by every request
    // served by the same Node process and is never torn down (onBeforeUnmount
    // does not run on the server). Browser only.
    if (typeof window === 'undefined') {
        return;
    }

    let counted = false;

    const sync = (open) => {
        if (open && ! counted) {
            openCount.value++;
            counted = true;
        } else if (! open && counted) {
            openCount.value--;
            counted = false;
        }
    };

    watch(
        () => (typeof isOpen === 'function' ? isOpen() : isOpen.value),
        (open) => sync(!! open),
        { immediate: true },
    );

    onBeforeUnmount(() => sync(false));
}
